import {
  deserializeSpaceDateRangeValue,
  filterClausesToSpaceParameterValues,
  isSpaceDateRangeValue,
  serializeSpaceDateRangeValue,
  spaceParameterValuesToFilterClauses,
  useRegisterSpaceParameterOpenClause,
  useSpaceParameterController,
  type SpaceParameterValues,
} from '@src/components/AppLayout/SpaceParameters';
import {
  DEFAULT_LOOKBACK_OPTIONS,
  FilterClauseType,
  notEmpty,
  useAccordionFilterBuilder,
  useFilterBuilder,
  type DateRangeOrLookback,
  type FilterBuilderSide,
  type FilterClause,
  type FilterableProperty,
  type FilterableSelectProperty,
  type UseFilterBuilderOutput,
} from '@talos/kyoko';
import { isEqual } from 'lodash-es';
import { useCallback, useEffect, useMemo, useRef, type Dispatch, type SetStateAction } from 'react';
import {
  DEFAULT_DEALER_SPACE_DATE_RANGE_VALUE,
  DealerSpaceParameterKey,
  getDisplayedDealerFilterBuilderSpaceParameterKeys,
  hasDealerSpaceDateRange,
  useDealerSpaceParameterProperties,
  type DealerFilterBuilderSpaceParameterKey,
} from './DealerSpaceParameters';

export const DEALER_SPACE_DATE_RANGE_SHORTCUTS = DEFAULT_LOOKBACK_OPTIONS.map(value => ({ value }));

interface UseDealerSpaceParametersControllerParams {
  controllerId: string;
  spaceParameterValues: SpaceParameterValues | undefined;
  onSpaceParameterValuesChanged?: (values: SpaceParameterValues) => void;
  openCustomerSelector?: () => void;
}

interface DealerSpaceParameterEditorParams {
  availableKeys: readonly string[];
  setValues: Dispatch<SetStateAction<SpaceParameterValues>>;
  values: SpaceParameterValues;
}

interface DealerSpaceFilterEditorParams extends DealerSpaceParameterEditorParams {
  propertiesByKey: Map<DealerFilterBuilderSpaceParameterKey, FilterableProperty>;
}

function applyFilterClausesToSpaceParameterValues(
  clausesByKey: Map<string, FilterClause>,
  keys: readonly DealerFilterBuilderSpaceParameterKey[],
  currentValues: SpaceParameterValues
) {
  const filterBuilderValues = filterClausesToSpaceParameterValues(clausesByKey, keys);
  const nextValues = { ...currentValues };
  for (const key of keys) {
    delete nextValues[key];
  }
  return { ...nextValues, ...filterBuilderValues };
}

function useSyncRenderedClauses(filterBuilder: UseFilterBuilderOutput, filterClauses: FilterClause[]) {
  const { filterClauses: renderedFilterClauses, resetFilterClauses } = filterBuilder;
  const priorFilterClauses = useRef(filterClauses);

  useEffect(() => {
    if (!isEqual(priorFilterClauses.current, filterClauses) && !isEqual(renderedFilterClauses, filterClauses)) {
      resetFilterClauses(filterClauses);
    }
    priorFilterClauses.current = filterClauses;
  }, [filterClauses, renderedFilterClauses, resetFilterClauses]);
}

// Customer/counterparty ignorant filter builder populated by the panels within the space of the controller.
function useDealerSpaceFilterBuilderEditor({
  filterClauses,
  keys,
  properties,
  setValues,
}: {
  filterClauses: FilterClause[];
  keys: readonly DealerFilterBuilderSpaceParameterKey[];
  properties: FilterableProperty[];
  setValues: Dispatch<SetStateAction<SpaceParameterValues>>;
}) {
  const handleFilterClausesChanged = useCallback(
    (clausesByKey: Map<string, FilterClause>) => {
      setValues(currentValues => applyFilterClausesToSpaceParameterValues(clausesByKey, keys, currentValues));
    },
    [keys, setValues]
  );
  const filterBuilder = useFilterBuilder({
    initialFilterClauses: filterClauses,
    properties,
    onFilterClausesChanged: handleFilterClausesChanged,
  });

  useSyncRenderedClauses(filterBuilder, filterClauses);

  return filterBuilder;
}

function useDealerSpaceDateRange({ availableKeys, setValues, values }: DealerSpaceParameterEditorParams) {
  const selectedDateRangeValue = values[DealerSpaceParameterKey.DateRange];
  const dateRangeValue = isSpaceDateRangeValue(selectedDateRangeValue)
    ? selectedDateRangeValue
    : DEFAULT_DEALER_SPACE_DATE_RANGE_VALUE;
  const dateRangePickerValue = useMemo(() => deserializeSpaceDateRangeValue(dateRangeValue), [dateRangeValue]);
  const handleDateRangeChanged = useCallback(
    (value: DateRangeOrLookback) => {
      setValues(currentValues => ({
        ...currentValues,
        [DealerSpaceParameterKey.DateRange]: serializeSpaceDateRangeValue(value),
      }));
    },
    [setValues]
  );

  return {
    dateRangePickerValue,
    handleDateRangeChanged,
    showDateRangePicker: hasDealerSpaceDateRange(availableKeys),
  };
}

/**
 * The customer selector is separated from the filter builder in the sense that it is on the left hand side and outside of the accordion.
 * We kind of separate it, but at the same time it references the same filter builder, so this is why the properties and clauses needs to be worked here with the boilerplate.
 */
function useDealerSpaceCustomerParameter({
  availableKeys,
  openCustomerSelector,
  propertiesByKey,
  setValues,
  values,
}: DealerSpaceFilterEditorParams & { openCustomerSelector?: () => void }) {
  const customerFilterBuilderKeys = useMemo(
    () =>
      availableKeys.includes(DealerSpaceParameterKey.Counterparty)
        ? ([DealerSpaceParameterKey.Counterparty] satisfies DealerFilterBuilderSpaceParameterKey[])
        : [],
    [availableKeys]
  );
  const customerFilterableProperties = useMemo<FilterableProperty[]>(
    () =>
      customerFilterBuilderKeys
        .map(key => {
          const property = propertiesByKey.get(key);
          return property == null ? undefined : { ...property, alwaysPresent: true, suppressInitialOpen: true };
        })
        .filter(notEmpty),
    [customerFilterBuilderKeys, propertiesByKey]
  );
  const customerFilterClauses = useMemo<FilterClause[]>(
    () =>
      customerFilterBuilderKeys.map(key => ({
        key,
        type: FilterClauseType.INCLUSIVE,
        selections:
          spaceParameterValuesToFilterClauses(values, customerFilterBuilderKeys).find(clause => clause.key === key)
            ?.selections ?? [],
      })),
    [customerFilterBuilderKeys, values]
  );
  const customerFilterBuilder = useDealerSpaceFilterBuilderEditor({
    filterClauses: customerFilterClauses,
    keys: customerFilterBuilderKeys,
    properties: customerFilterableProperties,
    setValues,
  });

  const maybeSelectedCustomers = customerFilterBuilderKeys.includes(DealerSpaceParameterKey.Counterparty)
    ? values[DealerSpaceParameterKey.Counterparty]
    : undefined;
  const selectedCustomers = Array.isArray(maybeSelectedCustomers)
    ? maybeSelectedCustomers.filter((value): value is string => typeof value === 'string')
    : undefined;
  const selectedCustomer = selectedCustomers?.length === 1 ? selectedCustomers[0] : undefined;
  const customerProperty = customerFilterableProperties[0] as FilterableSelectProperty | undefined;
  const handleCustomerChanged = useCallback(
    (customer: string | undefined) => {
      setValues(currentValues => {
        const nextValues = { ...currentValues };
        if (customer == null) {
          delete nextValues[DealerSpaceParameterKey.Counterparty];
          return nextValues;
        }
        return {
          ...nextValues,
          [DealerSpaceParameterKey.Counterparty]: [customer],
        };
      });
    },
    [setValues]
  );
  const openCustomerClause = useCallback(
    (side?: FilterBuilderSide, value?: string | string[]) => {
      if (customerFilterableProperties.length === 0) {
        return false;
      }
      if (openCustomerSelector != null) {
        if (value != null) {
          handleCustomerChanged(Array.isArray(value) ? value[0] : value);
        }
        openCustomerSelector();
        return true;
      }
      customerFilterBuilder.addAndOpenClause(DealerSpaceParameterKey.Counterparty, side, value);
      return true;
    },
    [customerFilterBuilder, customerFilterableProperties.length, handleCustomerChanged, openCustomerSelector]
  );

  return {
    customerFilterBuilder,
    customerProperty,
    handleCustomerChanged,
    openCustomerClause,
    selectedCustomer,
    selectedCustomers,
    showCustomerSelector: customerFilterableProperties.length > 0,
  };
}

function useDealerSpaceFilterBuilder({
  availableKeys,
  controllerId,
  propertiesByKey,
  setValues,
  values,
}: DealerSpaceFilterEditorParams & { controllerId: string }) {
  const filterBuilderKeys = useMemo(
    () =>
      getDisplayedDealerFilterBuilderSpaceParameterKeys(availableKeys, values).filter(
        key => key !== DealerSpaceParameterKey.Counterparty
      ),
    [availableKeys, values]
  );
  const filterableProperties = useMemo<FilterableProperty[]>(
    () => filterBuilderKeys.map(key => propertiesByKey.get(key)).filter(notEmpty),
    [filterBuilderKeys, propertiesByKey]
  );
  const filterClauses = useMemo(
    () => spaceParameterValuesToFilterClauses(values, filterBuilderKeys),
    [filterBuilderKeys, values]
  );
  const handleFilterClausesChanged = useCallback(
    (clausesByKey: Map<string, FilterClause>) => {
      setValues(currentValues =>
        applyFilterClausesToSpaceParameterValues(clausesByKey, filterBuilderKeys, currentValues)
      );
    },
    [filterBuilderKeys, setValues]
  );
  const filterBuilderAccordion = useAccordionFilterBuilder({
    accordionProps: { id: `${controllerId}-filters` },
    filterBuilderProps: {
      initialFilterClauses: filterClauses,
      properties: filterableProperties,
      onFilterClausesChanged: handleFilterClausesChanged,
    },
  });
  const {
    accordion: filterBuilderAccordionControls,
    filterBuilder,
    openClause: openFilterBuilderClause,
  } = filterBuilderAccordion;

  useSyncRenderedClauses(filterBuilder, filterClauses);

  return { filterBuilder, filterBuilderAccordion: filterBuilderAccordionControls, openFilterBuilderClause };
}

export function useDealerSpaceParametersController({
  controllerId,
  onSpaceParameterValuesChanged,
  openCustomerSelector,
  spaceParameterValues,
}: UseDealerSpaceParametersControllerParams) {
  const initialValues = useMemo(
    () => ({
      ...spaceParameterValues,
      [DealerSpaceParameterKey.DateRange]:
        spaceParameterValues?.[DealerSpaceParameterKey.DateRange] ?? DEFAULT_DEALER_SPACE_DATE_RANGE_VALUE,
    }),
    [spaceParameterValues]
  );
  const { active, availableKeys, setValues, values } = useSpaceParameterController(controllerId, initialValues);
  const propertiesByKey = useDealerSpaceParameterProperties();
  const dateRange = useDealerSpaceDateRange({ availableKeys, setValues, values });
  const customer = useDealerSpaceCustomerParameter({
    availableKeys,
    openCustomerSelector,
    propertiesByKey,
    setValues,
    values,
  });
  const { filterBuilder, filterBuilderAccordion, openFilterBuilderClause } = useDealerSpaceFilterBuilder({
    availableKeys,
    controllerId,
    propertiesByKey,
    setValues,
    values,
  });
  const { openCustomerClause } = customer;

  const openClause = useCallback(
    (property: string | undefined, side?: FilterBuilderSide, value?: string | string[]) => {
      if (property === DealerSpaceParameterKey.Counterparty && openCustomerClause(side, value)) {
        return;
      }
      openFilterBuilderClause(property, side, value);
    },
    [openCustomerClause, openFilterBuilderClause]
  );
  useRegisterSpaceParameterOpenClause(controllerId, openClause);

  // if active space and not equal to whats in config, sync to config.
  useEffect(() => {
    if (active && onSpaceParameterValuesChanged != null && !isEqual(spaceParameterValues ?? {}, values)) {
      onSpaceParameterValuesChanged(values);
    }
  }, [active, onSpaceParameterValuesChanged, spaceParameterValues, values]);

  return {
    customerFilterBuilder: customer.customerFilterBuilder,
    customerProperty: customer.customerProperty,
    dateRangePickerValue: dateRange.dateRangePickerValue,
    filterBuilder,
    filterBuilderAccordion,
    handleCustomerChanged: customer.handleCustomerChanged,
    handleDateRangeChanged: dateRange.handleDateRangeChanged,
    selectedCustomer: customer.selectedCustomer,
    selectedCustomers: customer.selectedCustomers,
    showCustomerSelector: customer.showCustomerSelector,
    showDateRangePicker: dateRange.showDateRangePicker,
  };
}
